// src/components/PobDetailsDoctorChemist.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../../css/PobDetailsDoctorChemist.css";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";

const PobDetailsDoctorChemist = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const location = useLocation();
  const search = React.useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);

  // Accept many possible query param names
  const rawMonthQuery =
    search.get("month") ||
    search.get("monthText") ||
    search.get("monthKey") ||
    search.get("monthLabel") ||
    null;
  const rawYearQuery = search.get("year") || null;
  const qsRetailerId =
    search.get("retailerId") || search.get("retailer_id") || null;

  // ---- FIXED: read type from either 'type' or 'retailer_type' (prefer 'type') ----
  const qsTypeRaw =
    search.get("type") || search.get("retailer_type") || null;
  const qsRetailerType = (qsTypeRaw || "").toString().trim().toLowerCase();

  // Build dynamic month name -> number mapping by using Date parsing for safety
  const monthNameToNumber = (() => {
    const base = {};
    const allShorts = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];
    allShorts.forEach((s) => {
      const dt = new Date(`${s} 1, 2000`);
      if (!Number.isNaN(dt.getTime())) base[s] = dt.getMonth() + 1;
      else {
        const idx = allShorts.indexOf(s);
        base[s] = idx + 1;
      }
    });
    return base;
  })();

  // robust parse function
  const parseMonth = (raw) => {
    if (raw == null) return null;
    const s = String(raw).trim();
    const asNum = Number(s);
    if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 12) return asNum;

    const letters = s.match(/[a-zA-Z]{3,}/);
    if (letters && letters[0]) {
      const k = letters[0].slice(0, 3).toLowerCase();
      if (monthNameToNumber[k]) return monthNameToNumber[k];
    }

    try {
      const dt = new Date(`${s} 1, 2000`);
      if (!Number.isNaN(dt.getTime())) return dt.getMonth() + 1;
    } catch {
      // ignore
    }

    return null;
  };

  const parsedMonth = parseMonth(rawMonthQuery);
  const parsedYear =
    rawYearQuery && Number.isFinite(Number(rawYearQuery))
      ? Number(rawYearQuery)
      : new Date().getFullYear();

  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!employeeId) {
        setError("Invalid or missing employee ID.");
        return;
      }

      setError(null);
      setIsLoading(true);

      try {
        // Build params and include both retailer_type and type for safety
        const params = {
          employee_id: Number(employeeId),
          today_only: false,
        };

        // Only include valid type values (doctor or chemist) — but still include if present
        if (qsRetailerType) {
          params.retailer_type = qsRetailerType;
          params.type = qsRetailerType;
        }

        // if retailer id present, attach (try numeric else string)
        if (qsRetailerId) {
          const rid = Number(qsRetailerId);
          params.retailer_id = Number.isFinite(rid) ? rid : qsRetailerId;
        }

        // only include valid numeric month/year
        if (parsedMonth != null) params.month = parsedMonth;
        if (parsedYear) params.year = parsedYear;

        // Debugging - show what we send
        // eslint-disable-next-line no-console
        console.log("Fetching POB details with params:", params);

        const res = await axios.get(
          `${BASE_URL}/api/combined-retailers/pob-details-doctor-chemist-stockistwise`,
          { params, headers: getAuthHeaders() }
        );

        setData(res.data || []);
      } catch (err) {
        console.error("POB fetch error:", err);
        const remote = err.response?.data;
        let msg = "Failed to fetch POB details.";
        if (remote) {
          if (typeof remote === "string") msg += ` ${remote}`;
          else if (remote.detail) msg += ` ${JSON.stringify(remote.detail)}`;
          else if (remote.message) msg += ` ${remote.message}`;
        } else {
          msg += ` ${err.message || ""}`;
        }
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // re-run when employeeId or location.search changes
  }, [employeeId, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit handlers
  const handleEditClick = (row) => {
    setEditingRowId(row.visit_id || row.id);
    setEditingValue(row.pob ?? 0);
  };

  const handleCancelClick = () => {
    setEditingRowId(null);
    setEditingValue("");
  };

  const handleSaveClick = async () => {
    if (!editingRowId) {
      alert("No visit selected to update");
      return;
    }

    const pobAmount = Number(editingValue);
    if (isNaN(pobAmount) || pobAmount < 0) {
      alert("Please enter a valid positive number for POB");
      return;
    }

    setIsLoading(true);
    const previousData = [...data];

    try {
      setData((prevData) =>
        prevData.map((row) =>
          row.visit_id === editingRowId || row.id === editingRowId
            ? { ...row, pob: pobAmount }
            : row
        )
      );

      const response = await axios.patch(
        `${BASE_URL}/api/visits/visit/${editingRowId}`,
        { pob_amount: pobAmount },
        { headers: getAuthHeaders() }
      );

      if (response.data) {
        setData((prevData) =>
          prevData.map((row) =>
            row.visit_id === editingRowId || row.id === editingRowId
              ? { ...row, ...response.data, pob: pobAmount }
              : row
          )
        );
      }

      setEditingRowId(null);
      setEditingValue("");
    } catch (err) {
      console.error("Error updating POB:", err);
      setData(previousData);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        "Failed to update POB. Please try again.";
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map((row, i) => ({
        "S.No": i + 1,
        Type: row.type,
        Code: row.code,
        Retailer: row.retailer || row.name || "",
        "Working Date": row.working_date || "",
        State: row.state || row.state_name || "",
        City: row.city || row.city_name || "",
        Area: row.area || row.area_name || "",
        Mobile: row.mobile || "",
        Address: row.address || "",
        POB: row.pob,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "POB Details");
    XLSX.writeFile(
      wb,
      `POB_Details_${employeeId || "NA"}_${parsedMonth != null ? parsedMonth : "all"}_${parsedYear}.xlsx`
    );
  };


  const totalPob = data.reduce((sum, row) => sum + Number(row.pob || 0), 0);

  // friendly display label
  const shortLabel = parsedMonth
    ? Object.keys(monthNameToNumber).find((k) => monthNameToNumber[k] === parsedMonth)
    : null;

  return (
    <div className="p-4 font-sans text-gray-800">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-red-700">
            POB Sheet - Employee ID: {employeeId}
          </h2>
          {(parsedMonth || rawMonthQuery) && (
            <div className="text-sm text-gray-600">
              Month: <span className="font-medium">{shortLabel ? shortLabel.toUpperCase() : rawMonthQuery}</span>{" "}
              | Year: <span className="font-medium">{parsedYear}</span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
      {isLoading && <div className="mb-2 text-sm text-gray-600">Loading...</div>}

        <div className="border rounded shadow  table-wrapper">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
            <tr className="bg-teal-100 text-left text-xs text-black leading-4">
              <th className="px-3 py-2 w-10">S.No</th>
              <th className="px-3 py-2 w-32">Date</th>
              <th className="px-3 py-2 w-20">Type</th>
              <th className="px-3 py-2">Retailer</th>
              <th className="px-3 py-2 w-28">City</th>
              <th className="px-3 py-2 w-28">Area</th>
              <th className="px-3 py-2 w-28">Phone</th>
              <th className="px-3 py-2 w-36">Stockist</th>
              <th className="px-3 py-2 w-28 text-right">POB</th>
              <th className="px-3 py-2 w-36">Action</th>
            </tr>
          </thead>

          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const isEditing = editingRowId === (item.visit_id || item.id);
                const zeroPob = Number(item.pob || 0) === 0;
                const rowBg = zeroPob
                  ? "bg-pink-100"
                  : index % 2 === 1
                  ? "bg-white"
                  : "bg-gray-50";

                return (
                  <tr
                    key={item.visit_id || item.id || index}
                    className={`${rowBg} align-middle`}
                  >
                    <td className="px-3 py-2">{index + 1}</td>
                    <td className="px-3 py-2">{item.working_date || ""}</td>
                    <td className="px-3 py-2">{item.type}</td>
                    <td className="px-3 py-2 font-medium text-blue-700 truncate">
                      {item.retailer || item.name}
                    </td>
                    <td className="px-3 py-2">{item.city || item.city_name}</td>
                    <td className="px-3 py-2">{item.area || item.area_name}</td>
                    <td className="px-3 py-2">{item.mobile}</td>
                    <td className="px-3 py-2">{item.stockist || ""}</td>

                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          className="w-24 text-right px-2 py-1 border rounded"
                          disabled={isLoading}
                        />
                      ) : (
                        Number(item.pob || 0).toFixed(2)
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {isEditing ? (
                        <div className="flex items-center space-x-2">
                          <CustomButton
                            variant="save"
                            onClick={handleSaveClick}
                            disabled={isLoading}
                          >
                            {isLoading ? "Saving..." : "Save"}
                          </CustomButton>

                          <CustomButton
                            variant="cancel"
                            onClick={handleCancelClick}
                            disabled={isLoading}
                          >
                            Cancel
                          </CustomButton>
                        </div>
                      ) : (
                        <CustomButton
                          variant="edit"
                          onClick={() => handleEditClick(item)}
                          disabled={isLoading}
                        >
                          EDIT
                        </CustomButton>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="10"
                  className="px-3 py-8 text-center text-gray-500"
                >
                  No records found.
                </td>
              </tr>
            )}
          </tbody>

          {data.length > 0 && (
            <tfoot>
              <tr className="bg-blue-800 text-white font-semibold">
                <td colSpan={8} className="px-3 py-2 text-right">
                  Total
                </td>
                <td className="px-3 py-2 text-right">{totalPob.toFixed(2)}</td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

     
      <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print"  className="whitespace-nowrap">
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExport}
                label="Export"
                variant="export"
                className="whitespace-nowrap"
              >
                📥
              </CustomButton>
            </div>

    </div>
  );
};

export default PobDetailsDoctorChemist;
